import { saveFileLocal } from './outbox';

/**
 * Save file content via the client-side outbox (write-ahead).
 *
 * Every save is written locally first (so closing the page never loses an
 * edit), then the outbox sync loop drains it to the backend.  There is no
 * synchronous retry loop anymore — a 409 is surfaced later as a `conflict`
 * entry, not thrown here.
 *
 * @param {string} path - Repo-relative file path
 * @param {string} content - Full markdown content (the new content)
 * @param {string|null} sha - Base git blob SHA (concurrency token from the
 *                            last read / last successful sync)
 * @param {number|null} version - Deprecated — retained for signature compat.
 * @param {Function} toast - Toast function (unused in the write-ahead path)
 * @param {boolean} isReorder - True when this save is a debounced reorder
 * @param {object|null} timeline - Optional timeline metadata (inline with save)
 * @param {string|null} baseContent - Pre-edit content (diff baseline for the
 *                                    first edit; ignored for later edits)
 * @returns {Promise<{sha: string|null}>} the entry's base sha (unchanged until sync)
 */
export async function saveWithRetry(path, content, sha, version, toast, isReorder = false, timeline = null, baseContent = null) {
  const entry = await saveFileLocal(path, content, baseContent, sha, timeline);
  return { sha: entry.baseSha ?? sha ?? null };
}

/** Return the local UTC offset as ``±HH:MM`` (e.g. ``+08:00``). */
function timezoneOffsetString(d) {
  const offMin = -d.getTimezoneOffset(); // minutes ahead of UTC
  const sign = offMin >= 0 ? '+' : '-';
  const abs = Math.abs(offMin);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
}

/**
 * Return the current local time-mark line in ``YYYY-MM-DD HH:MM±HH:MM``
 * format — an ISO-8601-style datetime whose trailing UTC offset tells the
 * reader which timezone the moment was recorded in.  This is the exact
 * first-line format the timeline uses to recognise a marked block.
 */
export function buildTimeMarkLine() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${date} ${time}${timezoneOffsetString(d)}`;
}

// Matches a time-mark line: ``YYYY-MM-DD HH:MM`` (legacy, no offset) or
// ``YYYY-MM-DD HH:MM±HH:MM`` (current, timezone-aware).  Group 1 = date,
// group 2 = time, group 3 = optional UTC offset.
const TIME_MARK_RE = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?:([+-]\d{2}:\d{2}))?$/;

/**
 * Prepend (or replace) the time-mark line at the top of a block's content.
 *
 * A block whose first line already is a time mark has that line replaced
 * (repeat-mark keeps a single, latest mark).  Otherwise the mark is prepended.
 *
 * @param {string} content - Original block content
 * @param {string} markLine - The mark line to apply
 * @returns {string} The marked content
 */
export function applyTimeMark(content, markLine) {
  const trimmed = (content ?? '').trim();
  if (!trimmed) return markLine || '';
  const lines = trimmed.split('\n');
  if (TIME_MARK_RE.test(lines[0].trim())) {
    lines[0] = markLine;
  } else {
    lines.unshift(markLine);
  }
  return lines.join('\n');
}

/** Parse a ``±HH:MM`` offset into signed minutes (null when absent). */
function parseOffsetMinutes(tzStr) {
  if (!tzStr) return null;
  const sign = tzStr[0] === '-' ? -1 : 1;
  const [hh, mm] = tzStr.slice(1).split(':').map(Number);
  return sign * (hh * 60 + mm);
}

/**
 * Format a parsed time mark into a badge label shown in the viewer's own
 * timezone.  The recorded ``±HH:MM`` offset is used to convert to the local
 * clock of the web/app; the offset itself is not displayed.  Legacy marks
 * (no offset) are interpreted as already-local and shown as-is.
 */
function formatTimeMarkBadge(dateStr, timeStr, tzStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = timeStr.split(':').map(Number);

  const offMin = parseOffsetMinutes(tzStr);
  const dt = offMin !== null
    ? new Date(Date.UTC(y, m - 1, d, hh, mm) - offMin * 60000)
    : new Date(y, m - 1, d, hh, mm);

  const pad = (n) => String(n).padStart(2, '0');
  const datePart = dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timePart = `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
  return `${datePart} · ${timePart}`;
}

/**
 * Split a block into its time-mark badge and the remaining body.
 *
 * When the block's first line is a time record (a ``YYYY-MM-DD HH:MM`` line,
 * optionally with a UTC offset), it is parsed into a readable datetime string
 * for a badge and the rest of the block is returned as ``body``.  Otherwise
 * ``badge`` is null and ``body`` is the whole (trimmed) content.
 *
 * @param {string} content - Block content
 * @returns {{ badge: string|null, body: string }}
 */
export function splitTimeMark(content) {
  const trimmed = (content ?? '').trim();
  const nl = trimmed.indexOf('\n');
  const first = nl === -1 ? trimmed : trimmed.slice(0, nl);
  const m = TIME_MARK_RE.exec(first.trim());
  if (!m) return { badge: null, body: trimmed };
  const body = nl === -1 ? '' : trimmed.slice(nl + 1).trim();
  return { badge: formatTimeMarkBadge(m[1], m[2], m[3]), body };
}
